import { type LexicalText, type Task } from "@/schema"
import { useDataTableStore } from "@/stores/data-table-store"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { useDebounce } from "@/hooks/use-debounce"
import {
  getFormFieldsRequirementFromError,
  openFormFieldsRequirementDialog,
} from "@/components/projects/form-fields-requirement"
import {
  getOutcomeRequirementFromError,
  openOutcomeRequirementDialog,
} from "@/components/projects/outcome-requirements"

import type { GroupedTasksResponse } from "./api"
import { invalidateTaskQueries, useCompleteTask, useUncompleteTask } from "./mutations"

interface InfiniteQueryData {
  pages: GroupedTasksResponse[]
  pageParams: unknown[]
}

export function useTaskCompletion() {
  const queryClient = useQueryClient()
  const { searchTerm } = useDataTableStore()
  const { mutate: completeTaskMutation, mutateAsync: completeTaskMutationAsync } = useCompleteTask()
  const { mutate: uncompleteTaskMutation } = useUncompleteTask()

  const debouncedInvalidate = useDebounce((task: Task) => {
    queryClient.setQueriesData(
      { queryKey: ["tasks", "grouped", searchTerm] },
      (oldData: InfiniteQueryData | undefined) => {
        if (!oldData?.pages) return oldData

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            groups: Object.fromEntries(
              Object.entries(page.groups).map(([groupKey, groupData]) => [
                groupKey,
                {
                  ...groupData,
                  tasks: groupData.tasks.map((t) => (t.id === task.id ? { ...t, ...task } : t)),
                },
              ])
            ),
          })),
        }
      }
    )

    invalidateTaskQueries(queryClient, task)
  }, 3000)

  const updateTaskStatus = (taskId: string, status: "done" | "todo", completedAt: string | null) => {
    queryClient.setQueriesData(
      { queryKey: ["tasks", "grouped", searchTerm] },
      (oldData: InfiniteQueryData | undefined) => {
        if (!oldData?.pages) return oldData

        return {
          ...oldData,
          pages: oldData.pages.map((page) => ({
            ...page,
            groups: Object.fromEntries(
              Object.entries(page.groups).map(([groupKey, groupData]) => [
                groupKey,
                {
                  ...groupData,
                  tasks: groupData.tasks.map((t) =>
                    t.id === taskId
                      ? {
                          ...t,
                          status,
                          completed_at: completedAt,
                          optimistic_status: status,
                        }
                      : t
                  ),
                },
              ])
            ),
          })),
        }
      }
    )

    const taskUpdate = { status, completed_at: completedAt, optimistic_status: status }

    queryClient.setQueryData(["task", taskId], (oldData: Task | undefined) => {
      if (!oldData) return oldData
      return { ...oldData, ...taskUpdate }
    })

    for (const entity of ["household", "project", "contact", "business", "trust", "person"]) {
      queryClient.setQueriesData({ queryKey: ["tasks", entity] }, (oldData: { tasks: Task[] } | undefined) => {
        if (!oldData?.tasks) return oldData
        return {
          ...oldData,
          tasks: oldData.tasks.map((t) => (t.id === taskId ? { ...t, ...taskUpdate } : t)),
        }
      })
    }
  }

  const handleTaskComplete = (taskId: string | undefined, completed: boolean, content?: LexicalText) => {
    if (!taskId) return

    updateTaskStatus(taskId, completed ? "done" : "todo", completed ? new Date().toISOString() : null)

    if (completed) {
      const successMessage = content ? "Task completed with comment" : "Task completed"
      const completePayload = content ? { id: taskId, content } : taskId

      completeTaskMutation(completePayload, {
        onSuccess: (data) => {
          toast.success(successMessage)
          debouncedInvalidate(data)
        },
        onError: (error) => {
          const outcomeRequirement = getOutcomeRequirementFromError(error)

          if (outcomeRequirement) {
            updateTaskStatus(taskId, "todo", null)

            openOutcomeRequirementDialog({
              requirement: outcomeRequirement,
              onConfirm: async ({ outcomeId, projectComment, formResponses }) => {
                try {
                  const data = await completeTaskMutationAsync({
                    id: taskId,
                    ...(content && { content }),
                    outcome_id: outcomeId,
                    ...(projectComment && { project_comment: projectComment }),
                    ...(formResponses && { form_responses: formResponses }),
                  })

                  updateTaskStatus(taskId, "done", new Date().toISOString())
                  toast.success(successMessage)
                  debouncedInvalidate(data)
                } catch (retryError) {
                  updateTaskStatus(taskId, "todo", null)
                  throw retryError
                }
              },
            })
            return
          }

          const formFieldsRequirement = getFormFieldsRequirementFromError(error)

          if (formFieldsRequirement) {
            updateTaskStatus(taskId, "todo", null)

            openFormFieldsRequirementDialog({
              requirement: formFieldsRequirement,
              onConfirm: async (formResponses) => {
                try {
                  const data = await completeTaskMutationAsync({
                    id: taskId,
                    ...(content && { content }),
                    form_responses: formResponses,
                  })

                  updateTaskStatus(taskId, "done", new Date().toISOString())
                  toast.success(successMessage)
                  debouncedInvalidate(data)
                } catch (retryError) {
                  updateTaskStatus(taskId, "todo", null)
                  throw retryError
                }
              },
            })
            return
          }

          updateTaskStatus(taskId, "todo", null)
        },
      })

      return
    }

    uncompleteTaskMutation(taskId, {
      onSuccess: (data) => {
        toast.success("Task marked as incomplete")
        debouncedInvalidate(data)
      },
      onError: () => {
        updateTaskStatus(taskId, "done", new Date().toISOString())
      },
    })
  }

  return { handleTaskComplete }
}
